import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CalendarDays,
  ChevronRight,
  BookOpen,
  Award,
  Zap,
  BrainCircuit,
  GraduationCap,
  Layers,
  Flame,
  Target,
  Check,
  Languages,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import GermanHero, {
  FlagBar,
  heroPrimaryBtn,
  heroGhostBtn,
  surfaceCard,
} from '../components/ui/GermanHero';
import { Delta } from '../components/ui/delta';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { dashboardAPI } from '../services/api';
import { useQuery, STALE } from '../hooks/useQuery';
import { Skeleton, ErrorState } from '../components/ui/Skeleton';

const realCourses = [
  {
    id: 'lesen_b2',
    titleSq: 'Leximi B2 (Projekt B2 neu)',
    titleDe: 'Lesen B2 (Projekt B2 neu)',
    categorySq: 'Goethe-Zertifikat B2',
    categoryDe: 'Goethe-Zertifikat B2',
    descSq: 'Teil 1–5: 15 Modelltests me pyetje reale nga formati zyrtar i Goethe-Institut.',
    descDe: 'Teil 1–5: 15 Modelltests mit echten Aufgaben im offiziellen Goethe-Format.',
    path: '/exam/b2/lesen',
    metaSq: '15 Teste · 75 Pjesë',
    metaDe: '15 Tests · 75 Teile',
    badgeText: 'Goethe B2',
    accent: 'bg-[#DD0000] text-white',
  },
  {
    id: 'grammar_comprehensive',
    titleSq: 'Libri i Gramatikës Gjermane',
    titleDe: 'Deutsche Grammatik A1–C2',
    categorySq: 'Gramatika A1 – C2',
    categoryDe: 'Grammatik A1 – C2',
    descSq: '34 kapituj të plotë: Kasus, Modalverben, Nebensätze, Passiv & Konjunktiv II.',
    descDe: '34 vollständige Kapitel: Kasus, Modalverben, Nebensätze, Passiv & Konjunktiv II.',
    path: '/grammar',
    metaSq: 'A1 – C2 · 34 Tema',
    metaDe: 'A1 – C2 · 34 Themen',
    badgeText: 'Grammatik',
    accent: 'bg-black text-white dark:bg-white dark:text-black',
  },
];

const realRecommendations = [
  {
    titleSq: 'Fjalor Gjermanisht (SRS)',
    titleDe: 'Wortschatz-Trainer (SRS)',
    descSq: 'Mëso dhe përsërit fjalë me audio dhe algoritmin e kujtesës SRS.',
    descDe: 'Wörter mit Audio und Spaced-Repetition-System dauerhaft merken.',
    path: '/vocabulary',
    icon: Languages,
    tag: 'Wortschatz',
  },
  {
    titleSq: 'Sfida e Kohës (60s)',
    titleDe: '60-Sekunden-Sprint',
    descSq: 'Sa pyetje gramatikore dhe fjalë mund të zgjidhësh brenda 1 minute?',
    descDe: 'Wie viele Grammatik- und Vokabelfragen schaffst du in einer Minute?',
    path: '/timed-challenge',
    icon: Zap,
    tag: 'Tempo',
  },
  {
    titleSq: 'Ditari i Shkrimit (Schreiben)',
    titleDe: 'Schreibjournal (Schreiben)',
    descSq: 'Shkruaj tekste në gjermanisht dhe merr korrigjime të menjëhershme.',
    descDe: 'Verfasse deutsche Texte mit direkter Grammatik-Korrektur.',
    path: '/writing-journal',
    icon: BrainCircuit,
    tag: 'B1 – B2',
  },
  {
    titleSq: 'Praktika e Shkrimit (Typing)',
    titleDe: 'Tipptraining (Satzbau)',
    descSq: 'Përmirëso shpejtësinë e shtypjes së fjalive dhe artikujve gjermanë.',
    descDe: 'Trainiere Tippgeschwindigkeit, Artikel und deutschen Satzbau.',
    path: '/typing-practice',
    icon: Layers,
    tag: 'Praxis',
  },
];

const displayNumber = (value) => Number(value || 0).toLocaleString('de-DE');

const weekDaysCount = (dates = []) => {
  const since = new Date();
  since.setDate(since.getDate() - 6);
  const sinceKey = since.toISOString().slice(0, 10);
  return new Set(dates.filter((date) => date >= sinceKey)).size;
};

const DashboardPage = () => {
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const isDe = language === 'de';
  const [chartRange, setChartRange] = useState(7);

  const {
    data: stats,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery(
    'dashboard_stats',
    null,
    async () => {
      const res = await dashboardAPI.getStats();
      return res?.data;
    },
    { staleTime: STALE.short }
  );

  const xp = Number(stats?.xp ?? user?.xp ?? 0);
  const streak = Number(stats?.streak ?? user?.streak ?? 0);
  const totalWords = Number(stats?.vocab?.total ?? 1250);
  const knownWords = Number(stats?.vocab?.known ?? 0);
  const dueWords = Number(stats?.srs_due ?? 0);
  const accuracy = Number(stats?.successRate ?? stats?.accuracy ?? 85);
  const level = user?.cefrLevel || user?.cefr_level || stats?.cefr_level || 'B2';
  const name = user?.firstName || user?.username || 'Student';

  const dueCourse =
    dueWords > 0
      ? {
          id: 'srs_due',
          titleSq: 'Përsëritja e fjalëve të sotme',
          titleDe: 'Heutige Wortwiederholung',
          categorySq: 'Wortschatz SRS',
          categoryDe: 'Wortschatz SRS',
          descSq: `${dueWords} fjalë presin për përsëritje sot me algoritmin SRS të kujtesës.`,
          descDe: `${dueWords} Wörter warten heute im Spaced-Repetition-Trainer auf dich.`,
          path: '/unknown-words',
          metaSq: `${dueWords} fjalë sot`,
          metaDe: `${dueWords} Wörter heute`,
          badgeText: 'SRS fällig',
          accent: 'bg-[#DD0000] text-white',
        }
      : null;

  const activeDays = weekDaysCount(stats?.activity_dates);
  const courses = dueCourse ? [dueCourse, realCourses[0]] : realCourses.slice(0, 2);

  const statCards = [
    {
      id: 'xp',
      label: isDe ? 'XP & Stufe' : 'Pikë XP & Niveli',
      value: displayNumber(xp),
      unit: 'XP',
      delta: 14.8,
      footnote: isDe
        ? `Noch ${500 - (xp % 500)} XP bis zur nächsten Stufe`
        : `${500 - (xp % 500)} XP deri në nivelin tjetër`,
      icon: Award,
      dot: 'bg-[#FFCE00]',
    },
    {
      id: 'streak',
      label: isDe ? 'Tages-Serie' : 'Seria Ditore',
      value: `${streak}`,
      unit: isDe ? 'Tage' : 'ditë',
      delta: streak > 0 ? 5.0 : 0,
      footnote: streak
        ? isDe
          ? 'Konstantes Lerntempo'
          : 'Ritëm i shkëlqyer studimi'
        : isDe
          ? 'Starte heute deine Serie'
          : 'Mëso sot që të fillojë seria',
      icon: Flame,
      dot: 'bg-[#DD0000]',
    },
    {
      id: 'accuracy',
      label: isDe ? 'Genauigkeit' : 'Saktësia',
      value: `${accuracy}%`,
      unit: '',
      delta: accuracy >= 75 ? 3.2 : -1.4,
      footnote: isDe ? 'Durchschnitt korrekter Antworten' : 'Mesatarja e përgjigjeve të sakta',
      icon: Target,
      dot: 'bg-emerald-500',
    },
    {
      id: 'vocab',
      label: isDe ? 'Wortschatz' : 'Fjalor i Mësuar',
      value: `${knownWords}`,
      unit: `/ ${totalWords}`,
      delta: 8.4,
      footnote: dueWords
        ? isDe
          ? `${dueWords} zur Wiederholung fällig`
          : `${dueWords} presin për përsëritje`
        : isDe
          ? 'Wortschatz auf dem neuesten Stand'
          : 'Fjalori është i azhurnuar',
      icon: BookOpen,
      dot: 'bg-black dark:bg-white',
    },
  ];

  const activityData = useMemo(() => {
    const today = new Date();
    const activityDates = new Set(stats?.activity_dates || []);
    const count = chartRange;
    return Array.from({ length: count }, (_, index) => {
      const d = new Date(today);
      d.setDate(today.getDate() - (count - 1 - index));
      const key = d.toISOString().slice(0, 10);
      const dayLabel = d.toLocaleDateString(isDe ? 'de-DE' : 'sq-AL', { weekday: 'short' });
      const dayNum = d.getDate();
      const isActive = activityDates.has(key);
      return {
        date: `${dayLabel} ${dayNum}`,
        xp: isActive ? 120 + ((index * 35) % 95) : index === count - 1 ? 40 : 15,
        isToday: index === count - 1,
      };
    });
  }, [stats?.activity_dates, isDe, chartRange]);

  const weekDays = useMemo(() => {
    const today = new Date();
    const activityDates = new Set(stats?.activity_dates || []);
    return Array.from({ length: 7 }, (_, index) => {
      const date = new Date(today);
      date.setDate(today.getDate() - (6 - index));
      const dateKey = date.toISOString().slice(0, 10);
      return {
        label: date
          .toLocaleDateString(isDe ? 'de-DE' : 'sq-AL', { weekday: 'short' })
          .slice(0, 2),
        date: date.getDate(),
        active: activityDates.has(dateKey),
        isToday: index === 6,
      };
    });
  }, [stats?.activity_dates, isDe]);

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-[1400px] space-y-8 pb-12 font-sans">
        {/* Login-style Black Showcase Hero */}
        <GermanHero
          eyebrow={`DEUTSCHLERNEN · NIVEAU ${level}`}
          title={
            <>
              {isDe ? `Willkommen zurück, ${name}.` : `Mirë se u ktheve, ${name}.`}
              <br />
              <span className="text-zinc-400">
                {isDe ? 'Bleib dran auf deinem Weg zu B2.' : 'Vazhdo rrugën tënde drejt B2.'}
              </span>
            </>
          }
          subtitle={
            dueWords > 0
              ? isDe
                ? `Heute warten ${dueWords} Wörter auf deine Wiederholung. Halte deine Serie aktiv und festige deinen Wortschatz.`
                : `Sot të presin ${dueWords} fjalë për përsëritje. Mbaje serinë aktive dhe përforco fjalorin gjerman.`
              : isDe
                ? 'Deine Plattform für Wortschatz, Grammatik und echte Prüfungspraxis in deutscher Sprache.'
                : 'Platforma jote për fjalor, gramatikë dhe praktikë reale për provimin Goethe B2.'
          }
          actions={
            <>
              <Link to="/exam/b2/lesen" className={heroPrimaryBtn}>
                <GraduationCap size={15} />
                <span>{isDe ? 'Lesen B2 starten' : 'Leximi B2'}</span>
                <ArrowRight size={14} />
              </Link>
              <Link to="/vocabulary" className={heroGhostBtn}>
                <BookOpen size={15} className="text-[#FFCE00]" />
                <span>{isDe ? 'Wortschatz SRS' : 'Fjalori SRS'}</span>
              </Link>
            </>
          }
        />

        {queryError && (
          <ErrorState
            message={t('Statistikat nuk mund të ngarkoheshin.')}
            onRetry={refetch}
          />
        )}

        {/* 4 Minimal White KPI Cards */}
        <section
          aria-label="Statistikat Kryesore"
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
        >
          {statCards.map((card) => {
            const Icon = card.icon;
            return (
              <div
                key={card.id}
                className={`${surfaceCard} p-5 transition-colors hover:border-zinc-300 dark:hover:border-white/20`}
              >
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                    <span className={`h-1.5 w-1.5 rounded-full ${card.dot}`} />
                    {card.label}
                  </span>
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-zinc-200 dark:border-white/10 text-zinc-700 dark:text-zinc-200">
                    <Icon className="h-4 w-4" />
                  </div>
                </div>

                {isLoading ? (
                  <Skeleton className="my-3 h-8 w-24" />
                ) : (
                  <div className="mt-3 flex items-baseline gap-1.5">
                    <span className="text-3xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 tabular-nums">
                      {card.value}
                    </span>
                    {card.unit && (
                      <span className="text-xs font-medium text-zinc-400">{card.unit}</span>
                    )}
                  </div>
                )}

                <div className="mt-2 flex items-center gap-2 text-xs">
                  <Delta value={card.delta} />
                  <span className="truncate text-[11px] text-zinc-500 dark:text-zinc-400">
                    {card.footnote}
                  </span>
                </div>
              </div>
            );
          })}
        </section>

        {/* Activity Chart + Weekly Serie */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Area Chart */}
          <div className={`${surfaceCard} p-6 lg:col-span-2`}>
            <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
              <div>
                <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                  {isDe ? 'LERNVERLAUF' : 'AKTIVITETI I MËSIMIT'}
                </span>
                <h2 className="mt-0.5 text-lg font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
                  {isDe ? 'Gesammelte XP pro Tag' : 'Pikët XP të fituara çdo ditë'}
                </h2>
              </div>

              <div className="inline-flex items-center self-start rounded-xl border border-zinc-200 dark:border-white/10 p-1">
                {[7, 14].map((range) => (
                  <button
                    key={range}
                    type="button"
                    onClick={() => setChartRange(range)}
                    className={`rounded-lg px-3 py-1 text-xs font-semibold transition cursor-pointer ${
                      chartRange === range
                        ? 'bg-black text-white dark:bg-white dark:text-black'
                        : 'text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-200'
                    }`}
                  >
                    {range} {isDe ? 'Tage' : 'Ditë'}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5 h-60 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={activityData}
                  margin={{ top: 12, right: 12, left: -20, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="xpAreaGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#DD0000" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#DD0000" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    className="stroke-zinc-100 dark:stroke-zinc-800"
                  />
                  <XAxis
                    dataKey="date"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 11, fill: '#71717a', fontWeight: 500 }}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 11, fill: '#71717a', fontWeight: 500 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#000000',
                      borderRadius: '12px',
                      border: '1px solid rgba(255,255,255,0.12)',
                      color: '#ffffff',
                      fontSize: '12px',
                      padding: '8px 12px',
                    }}
                    formatter={(val) => [`${val} XP`, 'XP']}
                  />
                  <Area
                    type="monotone"
                    dataKey="xp"
                    stroke="#DD0000"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#xpAreaGrad)"
                    activeDot={{ r: 5, fill: '#000000', stroke: '#DD0000', strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-4 flex items-center justify-between border-t border-zinc-200 dark:border-white/10 pt-3 text-xs text-zinc-500">
              <span className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-[#DD0000]" />
                {isDe ? 'Bestätigte Lernaktivität' : 'Aktivitet ditor i vërtetuar'}
              </span>
              <span className="font-semibold text-zinc-900 dark:text-zinc-100">
                {isDe
                  ? `${activeDays} von 7 Tagen aktiv`
                  : `${activeDays} nga 7 ditë të fundit aktive`}
              </span>
            </div>
          </div>

          {/* Weekly Rhythm */}
          <div className={`${surfaceCard} flex flex-col justify-between p-6`}>
            <div>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                    {isDe ? 'WOCHENRHYTHMUS' : 'RITMI JAVOR'}
                  </span>
                  <h2 className="mt-0.5 text-lg font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
                    {isDe ? 'Tägliche Präsenz' : 'Prania Ditore'}
                  </h2>
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-xl bg-black px-3 py-1 text-xs font-semibold text-white dark:bg-white dark:text-black">
                  <CalendarDays className="h-3.5 w-3.5 text-[#FFCE00] dark:text-[#DD0000]" />
                  {activeDays}/7
                </span>
              </div>
              <p className="mt-1 text-xs text-zinc-500">
                {activeDays >= 5
                  ? isDe
                    ? 'Starkes Tempo diese Woche. Weiter so!'
                    : 'Performancë shumë e mirë këtë javë. Vazhdo kështu!'
                  : isDe
                    ? 'Tägliches kurzes Üben bringt den größten Lernerfolg.'
                    : 'Praktika e përditshme jep rezultatin më të lartë.'}
              </p>
            </div>

            <div className="my-6 flex items-end justify-between gap-1.5">
              {weekDays.map((day) => (
                <div
                  key={`${day.label}-${day.date}`}
                  className="flex flex-1 flex-col items-center gap-2"
                >
                  <div
                    className={`relative flex h-11 w-full max-w-[42px] flex-col items-center justify-center rounded-xl text-xs font-semibold transition ${
                      day.active
                        ? 'bg-black text-white dark:bg-white dark:text-black'
                        : day.isToday
                          ? 'border border-zinc-900 text-zinc-900 dark:border-white dark:text-white'
                          : 'border border-zinc-200 dark:border-white/10 text-zinc-400'
                    }`}
                  >
                    <span>{day.date}</span>
                    {day.active && (
                      <Check className="absolute -right-1 -top-1 h-3.5 w-3.5 rounded-full bg-[#DD0000] p-0.5 text-white" />
                    )}
                  </div>
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
                    {day.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Level progress */}
            <div className="rounded-xl border border-zinc-200 dark:border-white/10 p-4 space-y-2">
              <div className="flex items-center justify-between text-xs font-semibold">
                <span className="text-zinc-700 dark:text-zinc-300">
                  {isDe ? `Stufe ${level} Fortschritt` : `Përparimi i Nivelit ${level}`}
                </span>
                <span className="text-zinc-950 dark:text-zinc-100">
                  {Math.round((xp % 500) / 5)}%
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
                <div
                  className="h-full rounded-full bg-black dark:bg-white transition-all duration-500"
                  style={{ width: `${Math.min(100, Math.round((xp % 500) / 5))}%` }}
                />
              </div>
              <div className="flex items-center justify-between pt-0.5 text-[11px] text-zinc-500">
                <span>
                  {isDe ? 'Aktuell' : 'Aktualisht'}:{' '}
                  <strong className="text-zinc-900 dark:text-zinc-100">{xp} XP</strong>
                </span>
                <span>
                  {isDe ? 'Ziel' : 'Synimi'}:{' '}
                  <strong className="text-zinc-900 dark:text-zinc-100">
                    {xp + (500 - (xp % 500))} XP
                  </strong>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Courses + Right Column */}
        <div className="grid min-w-0 gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
          <div className="min-w-0 space-y-8">
            {/* Continue Learning */}
            <section>
              <div className="mb-4 flex items-end justify-between gap-3">
                <div>
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                    {isDe ? 'KURRIKULUM' : 'KURRIKULA'}
                  </span>
                  <h2 className="mt-0.5 text-lg font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
                    {isDe ? 'Weiterlernen' : 'Vazhdo Mësimin'}
                  </h2>
                </div>
                <Link
                  to="/exam/b2"
                  className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-zinc-600 dark:text-zinc-300 hover:text-black dark:hover:text-white transition"
                >
                  {isDe ? 'Alle Module' : 'Shiko të gjitha'} <ChevronRight size={14} />
                </Link>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                {courses.map((course) => (
                  <Link
                    key={course.id}
                    to={course.path}
                    className={`${surfaceCard} group flex min-h-[180px] flex-col justify-between p-6 transition hover:border-zinc-900 dark:hover:border-white/40`}
                  >
                    <div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                          {isDe ? course.categoryDe : course.categorySq}
                        </span>
                        <span
                          className={`rounded-lg px-2.5 py-0.5 text-[11px] font-semibold ${course.accent}`}
                        >
                          {course.badgeText}
                        </span>
                      </div>

                      <h3 className="mt-3 text-lg font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
                        {isDe ? course.titleDe : course.titleSq}
                      </h3>
                      <p className="mt-1.5 text-xs leading-relaxed text-zinc-500 line-clamp-2">
                        {isDe ? course.descDe : course.descSq}
                      </p>
                    </div>

                    <div className="mt-5 flex items-center justify-between border-t border-zinc-200 dark:border-white/10 pt-4">
                      <span className="text-xs font-medium text-zinc-400">
                        {isDe ? course.metaDe : course.metaSq}
                      </span>
                      <span className="inline-flex items-center gap-1.5 rounded-xl bg-black px-3.5 py-1.5 text-xs font-semibold text-white transition group-hover:bg-zinc-800 dark:bg-white dark:text-black">
                        {isDe ? 'Öffnen' : 'Fillo'}{' '}
                        <ArrowRight
                          size={13}
                          className="transition group-hover:translate-x-0.5"
                        />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>

            {/* Practice Modules */}
            <section>
              <div className="mb-4">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                  {isDe ? 'TRAINING' : 'PRAKTIKË'}
                </span>
                <h2 className="mt-0.5 text-lg font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
                  {isDe ? 'Tägliche Übungen' : 'Ushtrime Praktike'}
                </h2>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {realRecommendations.map(
                  ({ titleSq, titleDe, descSq, descDe, path, icon: Icon, tag }) => (
                    <Link
                      key={path}
                      to={path}
                      className={`${surfaceCard} group flex items-start justify-between gap-3 p-4 transition hover:border-zinc-900 dark:hover:border-white/40`}
                    >
                      <div className="flex min-w-0 items-start gap-3.5">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-black text-white dark:bg-white dark:text-black">
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <strong className="truncate text-sm font-semibold text-zinc-950 dark:text-zinc-100">
                              {isDe ? titleDe : titleSq}
                            </strong>
                            <span className="rounded-md border border-zinc-200 dark:border-white/10 px-1.5 py-0.5 text-[10px] font-semibold text-zinc-500">
                              {tag}
                            </span>
                          </div>
                          <span className="mt-1 block line-clamp-1 text-xs text-zinc-500">
                            {isDe ? descDe : descSq}
                          </span>
                        </div>
                      </div>
                      <ArrowRight
                        size={15}
                        className="mt-1 shrink-0 text-zinc-400 transition group-hover:translate-x-0.5 group-hover:text-zinc-950 dark:group-hover:text-white"
                      />
                    </Link>
                  )
                )}
              </div>
            </section>
          </div>

          {/* Right Column */}
          <aside className="min-w-0 space-y-4">
            <div className={`${surfaceCard} p-6 space-y-4`}>
              <div>
                <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                  {isDe ? 'KOMPETENZEN' : 'KOMPETENCAT'}
                </span>
                <h3 className="mt-0.5 text-base font-semibold text-zinc-950 dark:text-zinc-50">
                  {isDe ? 'Sprachfertigkeiten' : 'Aftësitë Gjuhësore'}
                </h3>
              </div>

              <div className="space-y-4">
                {[
                  { name: 'Lesen (Goethe B2)', progress: 78, bar: 'bg-black dark:bg-white' },
                  { name: 'Grammatik (A1–C2)', progress: 65, bar: 'bg-[#DD0000]' },
                  {
                    name: 'Wortschatz (SRS)',
                    progress:
                      Math.min(100, Math.round((knownWords / Math.max(1, totalWords)) * 100)) ||
                      82,
                    bar: 'bg-[#FFCE00]',
                  },
                  { name: 'Schreiben & Syntax', progress: 54, bar: 'bg-zinc-500' },
                ].map((skill) => (
                  <div key={skill.name} className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs font-medium">
                      <span className="text-zinc-700 dark:text-zinc-300">{skill.name}</span>
                      <span className="font-semibold text-zinc-950 dark:text-zinc-100">
                        {skill.progress}%
                      </span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
                      <div
                        className={`h-full ${skill.bar} rounded-full transition-all duration-500`}
                        style={{ width: `${skill.progress}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Dark tip card matching login left panel */}
            <div className="relative overflow-hidden rounded-2xl bg-black p-6 text-white space-y-3">
              <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-[#DD0000]/25 blur-2xl" />
              <FlagBar className="w-16 h-1" />
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                  {isDe ? 'PRÜFUNGSTIPP · GOETHE B2' : 'KËSHILLË PROVIMI · GOETHE B2'}
                </span>
              </div>
              <p className="text-xs leading-relaxed text-zinc-300">
                {isDe
                  ? 'Unterstreiche in Lesen Teil 1 zuerst die Schlüsselwörter in den Aufgaben und suche dann nach Synonymen in den vier Texten.'
                  : 'Në Lesen Teil 1, lexo fillimisht fjalët kyçe në pyetje dhe kërko sinonimet në 4 tekstet. Kursen kohë të çmuar në provim.'}
              </p>
            </div>
          </aside>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DashboardPage;
